// Запросы и действия ведомости.
//
// ## Прошлый срез остаётся на экране
//
// Правило 11: спиннер на месте уже показанного числа запрещён. Финансовый директор ходит по
// шестидесяти объектам подряд, и каждое переключение, гасящее сетку в скелет, отнимает у него
// ровно то, ради чего он сюда пришёл, — числа. Поэтому у всех запросов `placeholderData`.
//
// ## После создания гасится всё дерево раздела
//
// Массовое создание меняет и реестр периода, и итоги, и число людей в каждой строке списка.
// Подкладывать ответ в кэш точечно тут нечего: `GenerateOut` несёт отчёт по объектам, а не
// сетку. Сброс дороже на один запрос, зато числа на экране согласованы между собой.

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  addRow,
  approvePayroll,
  createPayroll,
  fetchEmployeeCard,
  fetchGrid,
  fetchRegistry,
  generatePayrolls,
  markPayrollPaid,
  recalculate,
  recalculatePeriod,
  returnPayrollToWork,
  secondApprovePayroll,
  setCells,
  setDeductionOverride,
  setRowRate,
  submitPayroll,
  updateRow,
  type CellsIn,
  type DeductionOverrideIn,
  type EmployeePayrollsOut,
  type GenerateIn,
  type GenerateOut,
  type LifecycleIn,
  type PayrollEmployeeOut,
  type PayrollGridOut,
  type PayrollIn,
  type PayrollOut,
  type PayrollRowIn,
  type PayrollRowOut,
  type PayrollRowPatchIn,
  type RecalcOut,
  type RecalcPeriodIn,
  type RecalcPeriodOut,
  type ReturnToWorkIn,
  type RowRateIn,
} from '@/shared/api/endpoints/payroll';
import {
  changeRateBulk,
  fetchIncompleteRate,
  listEmployeeAssignments,
  type BulkRateIn,
  type BulkRateOut,
  type IncompleteRateListOut,
} from '@/shared/api/endpoints/people';
import type { ListResponse } from '@/shared/api/types';
import { ApiError } from '@/shared/errors/api-error';
import type { Period } from '@/shared/time/period';
import { countLabel } from '@/shared/text/plural';
import { подтверждение } from '@/shared/ui/toast/подтверждение';

/**
 * Отбор реестра ведомостей. Тз, раздел 5.4.4: «Список с фильтрами (период, юридическое лицо,
 * заказчик, объект, тип, статус)». Период приходит отдельно: он же задаёт, какой месяц открыт.
 */
export interface RegistryFilterState {
  readonly status?: string;
  readonly legal_entity_id?: string;
  readonly customer_id?: string;
  /** Тип ведомости: `admin` — только аппарат управления, `site` — только объектовые. */
  readonly kind?: string;
}

export const payrollKeys = {
  все: ['payroll'] as const,
  реестр: (период: Period, отбор: RegistryFilterState) =>
    [
      'payroll',
      'registry',
      период.year,
      период.month,
      отбор.status ?? '',
      отбор.legal_entity_id ?? '',
      отбор.customer_id ?? '',
      отбор.kind ?? '',
    ] as const,
  сетка: (payrollId: string) => ['payroll', 'grid', payrollId] as const,
  человек: (employeeId: string, период: Period | null) =>
    ['payroll', 'employee', employeeId, период?.year ?? 'все', период?.month ?? 'все'] as const,
};

/**
 * Реестр ведомостей за месяц.
 *
 * Административные приезжают в том же ответе: у них нет объекта, но период тот же, и второй
 * запрос за ними означал бы вторую точку, где список может разойтись с итогами.
 */
export function useRegistry(
  период: Period,
  отбор: RegistryFilterState = {},
  включено = true,
): UseQueryResult<ListResponse<PayrollOut>> {
  return useQuery({
    queryKey: payrollKeys.реестр(период, отбор),
    queryFn: ({ signal }) =>
      fetchRegistry(
        {
          year: период.year,
          month: период.month,
          status: отбор.status,
          legal_entity_id: отбор.legal_entity_id,
          customer_id: отбор.customer_id,
          // «Только объектовые» сервер отдельным признаком не знает: административная — это
          // ведомость БЕЗ объекта, и отбор по ней делается здесь же, в списке.
          administrative_only: отбор.kind === 'admin' ? true : undefined,
        },
        { signal },
      ),
    enabled: включено,
    placeholderData: (прежние) => прежние,
  });
}

export function useSheetGrid(
  payrollId: string | null,
  включено = true,
): UseQueryResult<PayrollGridOut> {
  return useQuery({
    queryKey: payrollKeys.сетка(payrollId ?? 'нет'),
    queryFn: ({ signal }) => fetchGrid(payrollId!, { signal }),
    enabled: payrollId !== null && включено,
    placeholderData: (прежние) => прежние,
  });
}

/** Сводный экран по человеку: расчёты по юрлицам, суммы не складываются (сценарий 39). */
export function useEmployeeCard(
  employeeId: string | null,
  период: Period | null,
): UseQueryResult<EmployeePayrollsOut> {
  return useQuery({
    queryKey: payrollKeys.человек(employeeId ?? 'нет', период),
    queryFn: ({ signal }) =>
      fetchEmployeeCard(
        employeeId!,
        период === null ? {} : { year: период.year, month: период.month },
        { signal },
      ),
    enabled: employeeId !== null,
    placeholderData: (прежние) => прежние,
  });
}

/**
 * Сброс после действия. Гасится всё дерево ведомости: созданная ведомость меняет и реестр, и
 * итоги периода, и сводный экран по каждому попавшему в неё человеку.
 */
function useСброс() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: payrollKeys.все });
  };
}

/**
 * Отказ «успели изменить» (409) перечитывает ведомость сразу, а не ждёт, пока она устареет.
 *
 * Сервер просит открыть ведомость заново и повторить правку. Но открытая заново ведомость
 * полминуты считается свежей и берётся из кэша с той же устаревшей версией, и повтор получал
 * тот же отказ. Версия уходит из-под рук и без второго человека: пакетный пересчёт периода
 * переписывает ведомости одну за другой (окно пересчёта закрывают, а он идёт дальше), правка
 * одной ведомости переписывает человека в соседних по месяцу. Перечитанная сетка приносит
 * панелям новую версию, и повтор проходит с первого нажатия (20.10.2026).
 */
function useСбросПриКонфликте() {
  const сбросить = useСброс();
  return (ошибка: unknown) => {
    if (ApiError.is(ошибка) && ошибка.status === 409) {
      сбросить();
    }
  };
}

export function useCreatePayroll() {
  const сбросить = useСброс();
  return useMutation<PayrollOut, unknown, PayrollIn>({
    mutationFn: (body) => createPayroll(body),
    meta: подтверждение('Ведомость создана'),
    onSuccess: сбросить,
  });
}

/**
 * Массовое создание. Ключ операции приходит СНАРУЖИ и живёт столько же, сколько форма мастера:
 * двойное нажатие и повтор после обрыва обязаны прийти с тем же ключом, иначе у человека
 * заведётся вторая пачка из шестидесяти ведомостей.
 */
export function useGeneratePayrolls() {
  const сбросить = useСброс();
  return useMutation<GenerateOut, unknown, { body: GenerateIn; idempotencyKey: string }>({
    mutationFn: ({ body, idempotencyKey }) => generatePayrolls(body, idempotencyKey),
    meta: подтверждение<unknown, GenerateOut>((_ввод, итог) =>
      итог.created === 1 ? 'Ведомость создана' : `Ведомостей создано: ${итог.created}`,
    ),
    // `onSettled`, а не `onSuccess`: после НЕУДАЧИ реестр надо перечитать тем более. Человек,
    // прочитавший красную плашку, первым делом спрашивает «а создалось или нет», и ответить
    // на это можно только свежим состоянием, а не памятью о том, что мы отправляли.
    onSettled: сбросить,
  });
}

/**
 * Рабочий лист «чего не хватает, чтобы месяц посчитался».
 *
 * Отдельным ключом от реестра: он про назначения, а не про ведомости, и обновляется от других
 * действий — записи тарифа, а не пересчёта.
 */
export function useIncompleteRate(период: Period, объект: string | null) {
  return useQuery<IncompleteRateListOut, ApiError>({
    queryKey: ['payroll', 'incomplete-rate', период.year, период.month, объект ?? 'все'],
    queryFn: ({ signal }) =>
      fetchIncompleteRate({ year: период.year, month: период.month, siteId: объект }, signal),
  });
}

/**
 * Тарифы пачкой.
 *
 * Сбрасывает и лист, и весь раздел расчёта: заполненный тариф меняет то, что покажет пересчёт,
 * и оставить реестр со старыми нулями значило бы показать человеку, что его работа не дошла.
 */
export function useChangeRateBulk() {
  const сбросить = useСброс();
  const client = useQueryClient();
  return useMutation<BulkRateOut, ApiError, BulkRateIn>({
    mutationFn: (body) => changeRateBulk(body),
    meta: подтверждение<BulkRateIn, BulkRateOut>((_ввод, итог) => итог.summary),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['payroll', 'incomplete-rate'] });
      void client.invalidateQueries({ queryKey: ['people'] });
      сбросить();
    },
  });
}

/**
 * Пересчёт ВСЕХ ведомостей периода одним действием.
 *
 * Сбрасывает весь раздел, а не одну ведомость: пересчёт меняет и реестр периода, и сводку над
 * ним, и полосу проверки в каждой открытой ведомости. Подложив одну, мы получили бы экран, где
 * строка уже новая, а итог над ней старый.
 *
 * Подтверждение говорит про ДЕНЬГИ, а не про число пересчитанных ведомостей: человек нажал эту
 * кнопку, чтобы деньги появились, и «пересчитано 77» на его вопрос не отвечает.
 */
export function useRecalcPeriod() {
  const сбросить = useСброс();
  return useMutation<RecalcPeriodOut, unknown, RecalcPeriodIn>({
    mutationFn: (body) => recalculatePeriod(body),
    meta: подтверждение<RecalcPeriodIn, RecalcPeriodOut>((_ввод, итог) =>
      итог.sheets_with_money > 0
        ? `Деньги появились на ${countLabel(итог.sheets_with_money, ['объекте', 'объектах', 'объектах'])}`
        : 'Пересчитали, но денег не появилось: смотрите, чего не хватает',
    ),
    onSuccess: сбросить,
  });
}

/**
 * Ячейки, вводимые с клавиатуры, и пересчёт этого человека.
 *
 * Ответ несёт человека целиком, но в кэш он НЕ подкладывается точечно: правка меняет и итоги
 * ведомости, и полосу проверки, и строку в реестре периода. Подложив одного человека, мы
 * получили бы экран, где строка уже новая, а итог над ней — старый; в ведомости на 281 строку
 * такая четвёрка разъехавшихся чисел читается как ошибка расчёта. Мигания при этом нет: прошлый
 * срез остаётся на экране, пока едет свежий.
 */
export function useSetCells(payrollId: string) {
  const client = useQueryClient();
  const сбросить = useСброс();
  const приКонфликте = useСбросПриКонфликте();
  return useMutation<PayrollEmployeeOut, unknown, { employeeId: string; body: CellsIn }>({
    // Ключ нужен полосе действий: пока правка едет, на сервере лежат ещё не те числа, и
    // утверждать их нельзя. Без ключа очередь правок для неё невидима, и заслон становится
    // украшением — человек утвердит ведомость на секунду раньше, чем в неё легла его же премия.
    mutationKey: payrollKeys.все,
    // МОЛЧИТ намеренно: правка ячейки видна сразу — число в клетке и новый итог строки,
    // который кладётся в кэш до ответа по сетке. Подсказка на каждую правку в ведомости на
    // 281 строку заслонила бы собой сами числа.
    mutationFn: ({ employeeId, body }) => setCells(payrollId, employeeId, body),
    onSuccess: (человек) => {
      // Пересчитанный человек кладётся в кэш СРАЗУ, а не ждёт повторного запроса сетки. Правило
      // 11: человек ввёл премию и обязан увидеть новый итог в своей строке немедленно, а не
      // через круг по сети — тем более что круг этот на ведомости в 281 строку не мгновенный.
      client.setQueryData<PayrollGridOut>(payrollKeys.сетка(payrollId), (прежняя) => {
        if (прежняя === undefined) {
          return прежняя;
        }
        return {
          ...прежняя,
          employees: (прежняя.employees ?? []).map((пункт) =>
            пункт.id === человек.id ? человек : пункт,
          ),
        };
      });
      // А итоги ведомости и строка в реестре периода считаются сервером, и их надо перезапросить:
      // подменить их здесь значило бы завести второе место, где живёт сумма к выплате.
      сбросить();
    },
    onError: приКонфликте,
  });
}

/**
 * Разовое удержание по виду долга в этой ведомости. Пересчитанный человек кладётся в кэш
 * сразу, как и у правки ячеек: новый итог он обязан увидеть в своей строке немедленно.
 */
export function useSetDeductionOverride(payrollId: string) {
  const client = useQueryClient();
  const сбросить = useСброс();
  const приКонфликте = useСбросПриКонфликте();
  return useMutation<
    PayrollEmployeeOut,
    unknown,
    { employeeId: string; columnCode: string; body: DeductionOverrideIn }
  >({
    mutationKey: payrollKeys.все,
    // МОЛЧИТ намеренно: панель человека сама пишет под кнопкой, что встало и каким стал итог
    // к выплате. Всплывающее подтверждение сказало бы то же самое второй раз.
    mutationFn: ({ employeeId, columnCode, body }) =>
      setDeductionOverride(payrollId, employeeId, columnCode, body),
    onSuccess: (человек) => {
      client.setQueryData<PayrollGridOut>(payrollKeys.сетка(payrollId), (прежняя) => {
        if (прежняя === undefined) {
          return прежняя;
        }
        return {
          ...прежняя,
          employees: (прежняя.employees ?? []).map((пункт) =>
            пункт.id === человек.id ? человек : пункт,
          ),
        };
      });
      сбросить();
    },
    onError: приКонфликте,
  });
}

/** Пересчёт всей ведомости. Отчёт возвращается сводкой, сетка приезжает сбросом. */
export function useRecalculate(payrollId: string) {
  const сбросить = useСброс();
  return useMutation<RecalcOut, unknown, void>({
    mutationKey: payrollKeys.все,
    mutationFn: () => recalculate(payrollId),
    // Пересчёт меняет числа по всей ведомости разом, и с экрана не видно, ЧТО именно
    // изменилось: строки те же, суммы другие. Сервер возвращает сводку одной фразой — её и
    // показываем, вместо своего пересказа.
    meta: подтверждение<void, RecalcOut>((_ввод, итог) => итог.summary || 'Ведомость пересчитана'),
    onSuccess: сбросить,
  });
}

/** Часы, сумма оклада, вывод строки из расчёта с основанием. */
export function useUpdateRow() {
  const сбросить = useСброс();
  const приКонфликте = useСбросПриКонфликте();
  return useMutation<PayrollRowOut, unknown, { rowId: string; body: PayrollRowPatchIn }>({
    mutationKey: payrollKeys.все,
    mutationFn: ({ rowId, body }) => updateRow(rowId, body),
    // Вывод строки из расчёта молчать не имеет права: человек перестаёт получать деньги по
    // этому назначению, а на экране меняется только оттенок строки.
    meta: подтверждение<{ rowId: string; body: PayrollRowPatchIn }>(({ body }) =>
      body.is_excluded === true
        ? 'Строка выведена из расчёта'
        : body.is_excluded === false
          ? 'Строка возвращена в расчёт'
          : 'Строка изменена',
    ),
    onSuccess: сбросить,
    onError: приКонфликте,
  });
}

/** Тариф строки: основание обязательно, поля ввода самой ставки нет. */
export function useSetRowRate() {
  const сбросить = useСброс();
  const приКонфликте = useСбросПриКонфликте();
  return useMutation<PayrollRowOut, unknown, { rowId: string; body: RowRateIn }>({
    mutationKey: payrollKeys.все,
    mutationFn: ({ rowId, body }) => setRowRate(rowId, body),
    // Тариф — это деньги человека за месяц, и меняют его с основанием. Молчание здесь читается
    // как «не сохранилось», и тариф правят второй раз.
    meta: подтверждение('Тариф изменён', 'Основание видно в разборе строки'),
    onSuccess: сбросить,
    onError: приКонфликте,
  });
}

/**
 * Назначения выбранного человека — чтобы снять с них тариф при добавлении строки.
 *
 * Свой хук, а не чужой из раздела «Люди»: разделы не ходят друг к другу за хуками. Дверь общая
 * (`shared/api/endpoints/people`), и ключ кэша тот же, поэтому запрос всё равно уходит один.
 */
export function useAssignmentsForPayroll(employeeId: string | null) {
  return useQuery({
    queryKey: ['people', 'assignments', employeeId ?? 'нет'],
    queryFn: ({ signal }) => listEmployeeAssignments(employeeId!, signal),
    enabled: employeeId !== null,
  });
}

/** Новая тарифная строка человека. */
export function useAddRow(payrollId: string) {
  const сбросить = useСброс();
  const приКонфликте = useСбросПриКонфликте();
  return useMutation<PayrollRowOut, unknown, PayrollRowIn>({
    mutationKey: payrollKeys.все,
    mutationFn: (body) => addRow(payrollId, body),
    // Строка добавляется в ведомость на 281 человека и уезжает к своему сотруднику: с того
    // места, где человек нажимал, её не видно.
    meta: подтверждение('Строка добавлена'),
    onSuccess: сбросить,
    onError: приКонфликте,
  });
}

// ── Жизненный цикл ───────────────────────────────────────────────────────────────────────
//
// Четыре хука на четыре нажатия. Каждый после успеха гасит всё дерево ведомости: состояние
// меняет и шапку, и реестр, и права на правку ячеек — показать хоть что-то из прежнего
// значило бы дать человеку нажать по второму разу то, что уже сделано.

/** Пересчитать и отправить на согласование. */
export function useSubmitPayroll(payrollId: string) {
  const сбросить = useСброс();
  const приКонфликте = useСбросПриКонфликте();
  return useMutation<PayrollOut, unknown, LifecycleIn | void>({
    mutationFn: (body) => submitPayroll(payrollId, body ?? {}),
    meta: подтверждение('Ведомость отправлена на решение'),
    onSuccess: сбросить,
    onError: приКонфликте,
  });
}

/** Второй уровень согласования. */
export function useSecondApprovePayroll(payrollId: string) {
  const сбросить = useСброс();
  const приКонфликте = useСбросПриКонфликте();
  return useMutation<PayrollOut, unknown, LifecycleIn | void>({
    mutationFn: (body) => secondApprovePayroll(payrollId, body ?? {}),
    meta: подтверждение('Ведомость согласована'),
    onSuccess: сбросить,
    onError: приКонфликте,
  });
}

/** Утвердить: номер, снимок условий, проводка денег. */
export function useApprovePayroll(payrollId: string) {
  const сбросить = useСброс();
  const приКонфликте = useСбросПриКонфликте();
  return useMutation<PayrollOut, unknown, LifecycleIn | void>({
    mutationFn: (body) => approvePayroll(payrollId, body ?? {}),
    meta: подтверждение('Ведомость утверждена: деньги проведены'),
    onSuccess: сбросить,
    onError: приКонфликте,
  });
}

/** Отметить факт выплаты по ведомости. */
export function useMarkPayrollPaid(payrollId: string) {
  const сбросить = useСброс();
  const приКонфликте = useСбросПриКонфликте();
  return useMutation<PayrollOut, unknown, LifecycleIn | void>({
    mutationFn: (body) => markPayrollPaid(payrollId, body ?? {}),
    meta: подтверждение('Выплата отмечена'),
    onSuccess: сбросить,
    onError: приКонфликте,
  });
}

/** Вернуть утверждённую в работу: единственное действие цепочки с обязательным основанием. */
export function useReturnPayrollToWork(payrollId: string) {
  const сбросить = useСброс();
  const приКонфликте = useСбросПриКонфликте();
  return useMutation<PayrollOut, unknown, ReturnToWorkIn>({
    mutationFn: (body) => returnPayrollToWork(payrollId, body),
    meta: подтверждение('Ведомость вернулась в работу'),
    onSuccess: сбросить,
    onError: приКонфликте,
  });
}
